import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, apiError } from "@/lib/api";
import { Loader2, Send, Check, CheckCheck, Megaphone, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

function fmtTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch (_e) { return ""; }
}

/**
 * Admin-Dialog zum Senden von Nachrichten.
 *  - user gesetzt   → Einzelnachricht an diesen User (+ Verlauf mit ✓/✓✓)
 *  - user === null  → Broadcast an ALLE User (+ Verlauf mit "X/Y gelesen")
 */
export function UserMessageDialog({ open, onOpenChange, user }) {
  const isBroadcast = !user;
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      if (isBroadcast) {
        const { data } = await api.get("/admin/messages", { params: { broadcast: true } });
        setHistory(data.broadcasts || []);
      } else {
        const { data } = await api.get("/admin/messages", { params: { recipient_id: user.id } });
        setHistory(data.messages || []);
      }
    } catch (_e) {
      setHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (open) {
      setSubject("");
      setBody("");
      loadHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user?.id]);

  const send = async () => {
    if (!body.trim()) { toast.error("Bitte eine Nachricht eingeben"); return; }
    setSending(true);
    try {
      const payload = isBroadcast
        ? { to_all: true, subject, body }
        : { user_id: user.id, subject, body };
      const { data } = await api.post("/admin/messages", payload);
      toast.success(isBroadcast
        ? `Nachricht an ${data.recipient_count} Benutzer gesendet`
        : "Nachricht gesendet");
      setSubject("");
      setBody("");
      await loadHistory();
    } catch (e) {
      toast.error(apiError(e, "Senden fehlgeschlagen"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="user-message-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isBroadcast
              ? <><Megaphone className="h-4 w-4 text-violet-600" /> Nachricht an alle Benutzer</>
              : <><MessageSquare className="h-4 w-4 text-sky-600" /> Nachricht an {user?.name || user?.email}</>}
          </DialogTitle>
          <DialogDescription>
            {isBroadcast
              ? "Die Nachricht erscheint bei ALLEN Benutzern in der Benachrichtigungs-Glocke."
              : "Die Nachricht erscheint beim Benutzer in der Benachrichtigungs-Glocke."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Input
            placeholder="Betreff (optional)"
            value={subject}
            maxLength={200}
            onChange={(e) => setSubject(e.target.value)}
            data-testid="message-subject-input"
          />
          <Textarea
            placeholder="Nachricht schreiben…"
            value={body}
            rows={4}
            maxLength={5000}
            onChange={(e) => setBody(e.target.value)}
            data-testid="message-body-input"
          />
          <div className="flex justify-end">
            <Button
              onClick={send}
              disabled={sending || !body.trim()}
              className={cn(
                "text-white",
                isBroadcast
                  ? "bg-violet-600 hover:bg-violet-700"
                  : "bg-sky-600 hover:bg-sky-700",
              )}
              data-testid="message-send-btn"
            >
              {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              {isBroadcast ? "An alle senden" : "Senden"}
            </Button>
          </div>
        </div>

        {/* Verlauf mit Lese-Status */}
        <div className="border-t border-zinc-100 pt-3">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-zinc-500 mb-2">
            Gesendete Nachrichten
          </div>
          {loadingHistory ? (
            <div className="flex items-center gap-2 text-xs text-zinc-500 py-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Lade…
            </div>
          ) : history.length === 0 ? (
            <div className="text-xs text-zinc-400 py-2" data-testid="message-history-empty">
              Noch keine Nachrichten gesendet.
            </div>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto pr-1" data-testid="message-history">
              {history.map((m) => (
                <div
                  key={m.id || m.broadcast_id}
                  className="rounded-lg border border-zinc-100 bg-zinc-50/60 px-3 py-2"
                  data-testid={`message-history-item-${m.id || m.broadcast_id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      {m.subject && (
                        <div className="text-xs font-semibold text-zinc-900 truncate">{m.subject}</div>
                      )}
                      <div className="text-xs text-zinc-600 line-clamp-2 whitespace-pre-wrap">{m.body}</div>
                    </div>
                    <div className="shrink-0 text-right">
                      {isBroadcast ? (
                        <span
                          className={cn(
                            "inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full",
                            m.read_count >= m.total
                              ? "bg-sky-100 text-sky-700"
                              : "bg-zinc-100 text-zinc-500",
                          )}
                          title={`${m.read_count} von ${m.total} gelesen`}
                        >
                          <CheckCheck className="h-3 w-3" /> {m.read_count}/{m.total}
                        </span>
                      ) : m.read_at ? (
                        <span className="inline-flex items-center gap-0.5 text-sky-600" title={`Gelesen: ${fmtTime(m.read_at)}`}>
                          <CheckCheck className="h-3.5 w-3.5" />
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-zinc-400" title="Zugestellt, noch nicht gelesen">
                          <Check className="h-3.5 w-3.5" />
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-[10px] text-zinc-400 mt-1">{fmtTime(m.created_at)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
